<script setup lang="ts">
import PhIconButton from '@/components/ui/PhIconButton.vue'
import UICloseButton from '@/components/ui/UICloseButton.vue'
import UICollapseButton from '@/components/ui/UICollapseButton.vue'
import UIHeading from '@/components/ui/UIHeading.vue'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import useLanguage from '@/composables/useLanguage'
import UIBody from '@lifk/ui-core/UIBody.vue'
import { PhCaretLeft, PhHouse, PhMapTrifold } from '@phosphor-icons/vue'
import { computed } from 'vue'

withDefaults(
  defineProps<{
    /** What text should be displayed in the header? */
    text: string
    /** Where is the header located (affects spacing)? */
    location?: keyof typeof locationMapping
    /** What variant should be used for the header coloration? */
    variant?: keyof typeof variantMapping
    /** What should the heading font size be? */
    headingSize?: 'serif' | 'serif-sm' | 'serif-xs'
    /** What HTML tag should the heading render as? */
    headingTag?: string
    /** If provided, show a back button on the left, centring the title (emits `back`). */
    backBtnVariant?: 'default' | 'toHome' | 'toProject'
    /** What type of button should be included on the right, if any? */
    btnVariant?: 'close' | 'collapse' | 'expand'
    /** What should the aria label be for the right button (if applicable)? */
    buttonAriaLabel?: string
  }>(),
  {
    location: 'modal',
    variant: 'default',
    headingSize: 'serif',
    headingTag: 'h4'
  }
)

defineEmits<{
  (e: 'btnClicked'): void
  (e: 'back'): void
}>()

const i18n = useLanguage('UIHeader')
const isMobile = computed(() => useDetectBreakpoint('xs'))

const locationMapping = {
  modal: 'p-padding-panel-container-horz sm:pt-padding-panel-container-vert',
  panel: 'px-padding-panel-container-horz pt-4 pb-stack-default',
  draggablePanel: 'px-padding-panel-container-horz py-stack-default sm:pt-4',
  mobileSiteSelector:
    'px-padding-panel-container-horz pt-padding-panel-container-vert pb-stack-relaxed space-y-5',
  legendCollapsed:
    'px-padding-default py-0.5 sm:px-padding-panel-container-horz sm:py-2'
}

const variantMapping = {
  default: {
    container: '',
    textColor: 'text-default'
  },
  warn: {
    container: 'bg-warning',
    textColor: 'text-emphasis'
  },
  destructive: {
    container: 'bg-error',
    textColor: 'text-inverse'
  }
}
</script>

<template>
  <header
    class="w-full sm:rounded-t"
    :class="[locationMapping[location], variantMapping[variant].container]"
  >
    <div class="flex items-center justify-between space-x-2">
      <PhIconButton
        v-if="backBtnVariant === 'default'"
        :tooltip-text="i18n.back"
        tooltip-direction="bottom"
        tooltip-alignment="center"
        @click="$emit('back')"
      >
        <PhCaretLeft size="24" />
      </PhIconButton>
      <button
        v-else-if="backBtnVariant"
        class="w-fit text-charcoal flex items-center space-x-inline-default"
        :aria-label="
          backBtnVariant === 'toHome' ? i18n.backToHome : i18n.backToProject
        "
        @click="$emit('back')"
      >
        <PhCaretLeft size="16" aria-hidden="true" />
        <component
          :is="backBtnVariant === 'toHome' ? PhHouse : PhMapTrifold"
          size="24"
          aria-hidden="true"
        />
        <UIBody
          :text="
            backBtnVariant === 'toHome' ? i18n.backToHome : i18n.backToProject
          "
        />
      </button>
      <UIHeading
        v-if="location !== 'mobileSiteSelector'"
        :tag="headingTag"
        :variant="headingSize"
        :text="text"
        class="flex-1"
        :class="[
          variantMapping[variant].textColor,
          {
            // back button (with or without right button)
            'text-center': backBtnVariant,
            // only right button
            'text-left': !backBtnVariant && btnVariant,
            // no buttons
            'text-center sm:text-left': !backBtnVariant && !btnVariant,
            // larger text vertical spacing correction
            '-mt-0.5': headingSize === 'serif'
          }
        ]"
      />
      <UICloseButton
        v-if="btnVariant === 'close'"
        tool-tip-direction="bottom"
        :color="variant === 'destructive' ? 'inverse' : 'default'"
        :aria-label="buttonAriaLabel"
        @close="$emit('btnClicked')"
      />
      <UICollapseButton
        v-else-if="btnVariant"
        :variant="btnVariant"
        :aria-label="buttonAriaLabel"
        :size-px="location === 'legendCollapsed' && isMobile ? 16 : 24"
        @click="$emit('btnClicked')"
      />
    </div>
    <UIHeading
      v-if="location === 'mobileSiteSelector'"
      :tag="headingTag"
      :variant="headingSize"
      :text="text"
      :class="variantMapping[variant].textColor"
    />
  </header>
</template>
